import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { SVGIcon } from '@instructure/ui-svg-images';
import { IconCheckMarkSolid } from '@instructure/ui-icons';
import { themeable } from '@instructure/ui-themeable';
var styles = {
  componentId: 'yyQPt',
  template: function template(theme) {
    return "\n\n.yyQPt_bGBk{align-items:flex-start;display:flex}\n\n.yyQPt_cSXm{-webkit-margin-end:".concat(theme.marginRight || 'inherit', ";-webkit-margin-start:0;align-items:center;background:").concat(theme.background || 'inherit', ";border:").concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";border-radius:").concat(theme.borderRadius || 'inherit', ";box-sizing:border-box;color:").concat(theme.color || 'inherit', ";display:flex;flex-shrink:0;justify-content:center;margin-inline-end:").concat(theme.marginRight || 'inherit', ";margin-inline-start:0;padding:").concat(theme.padding || 'inherit', ";position:relative;transition:all 0.2s}\n\n[dir=ltr] .yyQPt_cSXm{margin-left:0;margin-right:").concat(theme.marginRight || 'inherit', "}\n\n[dir=rtl] .yyQPt_cSXm{margin-left:").concat(theme.marginRight || 'inherit', ";margin-right:0}\n\n.yyQPt_cSXm:before{border:").concat(theme.focusBorderWidth || 'inherit', " ").concat(theme.focusBorderStyle || 'inherit', " ").concat(theme.focusBorderColor || 'inherit', ";border-radius:calc(").concat(theme.borderRadius || 'inherit', "*1.5);bottom:-0.3125rem;box-sizing:border-box;content:\"\";left:-0.3125rem;opacity:0;pointer-events:none;position:absolute;right:-0.3125rem;top:-0.3125rem;transform:scale(0.75);transition:all 0.2s}\n\n.yyQPt_blJt{color:").concat(theme.labelColor || 'inherit', ";flex:1 1 auto;font-family:").concat(theme.labelFontFamily || 'inherit', ";font-weight:").concat(theme.labelFontWeight || 'inherit', ";line-height:").concat(theme.labelLineHeight || 'inherit', ";min-width:0.0625rem}\n\n.yyQPt_doqw .yyQPt_blJt{font-size:").concat(theme.labelFontSizeSmall || 'inherit', "}\n\n.yyQPt_doqw .yyQPt_cSXm{font-size:").concat(theme.iconSizeSmall || 'inherit', ";height:").concat(theme.facadeSizeSmall || 'inherit', ";width:").concat(theme.facadeSizeSmall || 'inherit', "}\n\n.yyQPt_ycrn .yyQPt_blJt{font-size:").concat(theme.labelFontSizeMedium || 'inherit', "}\n\n.yyQPt_ycrn .yyQPt_cSXm{font-size:").concat(theme.iconSizeMedium || 'inherit', ";height:").concat(theme.facadeSizeMedium || 'inherit', ";width:").concat(theme.facadeSizeMedium || 'inherit', "}\n\n.yyQPt_cMDj .yyQPt_blJt{font-size:").concat(theme.labelFontSizeLarge || 'inherit', "}\n\n.yyQPt_cMDj .yyQPt_cSXm{font-size:").concat(theme.iconSizeLarge || 'inherit', ";height:").concat(theme.facadeSizeLarge || 'inherit', ";width:").concat(theme.facadeSizeLarge || 'inherit', "}\n\n.yyQPt_cjfS .yyQPt_cSXm{background:").concat(theme.checkedBackground || 'inherit', ";border-color:").concat(theme.checkedBorderColor || 'inherit', "}\n\n.yyQPt_cjfS .yyQPt_blJt{color:").concat(theme.checkedLabelColor || 'inherit', "}\n\n.yyQPt_cVYB .yyQPt_cSXm:before{opacity:1;transform:scale(1)}\n\n.yyQPt_Ffcq .yyQPt_cSXm{border-color:").concat(theme.hoverBorderColor || 'inherit', "}");
  },
  'root': 'yyQPt_bGBk',
  'facade': 'yyQPt_cSXm',
  'label': 'yyQPt_blJt',
  'small': 'yyQPt_doqw',
  'medium': 'yyQPt_ycrn',
  'large': 'yyQPt_cMDj',
  'checked': 'yyQPt_cjfS',
  'focused': 'yyQPt_cVYB',
  'hovered': 'yyQPt_Ffcq'
};
import theme from './theme';
/**
---
parent: Checkbox
---
**/

var _ref = /*#__PURE__*/React.createElement(SVGIcon, {
  viewBox: "0 0 1920 1920",
  inline: false
}, /*#__PURE__*/React.createElement("rect", {
  x: "140",
  y: "820",
  width: "1640",
  height: "280"
}));

var _ref2 = /*#__PURE__*/React.createElement(IconCheckMarkSolid, {
  inline: false
});

var CheckboxFacade = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(CheckboxFacade, _Component);

  var _super = _createSuper(CheckboxFacade);

  function CheckboxFacade() {
    _classCallCheck(this, CheckboxFacade);

    return _super.apply(this, arguments);
  }

  _createClass(CheckboxFacade, [{
    key: "renderIcon",
    value: function renderIcon() {
      if (this.props.indeterminate) {
        return _ref;
      } else if (this.props.checked) {
        return _ref2;
      } else {
        return null;
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var _this$props = this.props,
          size = _this$props.size,
          checked = _this$props.checked,
          focused = _this$props.focused,
          hovered = _this$props.hovered,
          indeterminate = _this$props.indeterminate;
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.checked, checked || indeterminate), _defineProperty(_classes, styles.focused, focused), _defineProperty(_classes, styles.hovered, hovered), _defineProperty(_classes, styles[size], true), _classes);
      return /*#__PURE__*/React.createElement("span", {
        className: classnames(classes)
      }, /*#__PURE__*/React.createElement("span", {
        className: styles.facade,
        "aria-hidden": "true"
      }, this.renderIcon()), /*#__PURE__*/React.createElement("span", {
        className: styles.label
      }, this.props.children));
    }
  }]);

  CheckboxFacade.displayName = "CheckboxFacade";
  return CheckboxFacade;
}(Component), _class2.propTypes = {
  children: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  focused: PropTypes.bool,
  hovered: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
  * Visual state showing that child checkboxes are a combination of checked and unchecked
  */
  indeterminate: PropTypes.bool
}, _class2.defaultProps = {
  checked: false,
  focused: false,
  hovered: false,
  size: 'medium',
  indeterminate: false
}, _temp)) || _class);
export default CheckboxFacade;
export { CheckboxFacade };